import { animate, style, transition, trigger } from '@angular/animations';
import { HttpErrorResponse } from '@angular/common/http';
import {
    AfterViewInit,
    Component,
    ElementRef,
    Input,
    OnChanges,
    OnInit,
    QueryList,
    ViewChildren
} from '@angular/core';
import {
    UntypedFormArray,
    UntypedFormBuilder,
    UntypedFormGroup,
    Validators
} from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { IMunicipio } from '@app/modules/parametrizacao/municipio/model/municipio.model';
import { MunicipioService } from '@app/modules/parametrizacao/municipio/services/municipio.service';
import {
    ConfirmDialogComponent,
    ConfirmDialogData
} from '@app/shared/components';
import { CepPipe } from '@app/shared/pipes';
import { merge } from 'rxjs';
import {
    CepService,
    CustomValidators,
    ErroMessages,
    FormServiceValidationMessages,
    IErroMessages,
    Pattern,
    TokenService,
    Utils,
    ViggoCoreCustomInputComponent
} from 'viggo-sistemas-core-web';
import { IParceiro, IParceiroEndereco } from '../../model';
import { locationValidationMessages } from '../../validation.message';

@Component({
  selector: 'parceiro-form-address',
  templateUrl: './form-address.html',
  styleUrls: ['./form-address.scss'],
  providers: [MunicipioService, CepService],
  animations: [
    trigger('fade', [
      transition(':enter', [
        style({ opacity: 0, height: 0 }),
        animate('0.2s', style({ opacity: 1, height: '*' }))
      ]),
      transition(':leave', [
        style({ height: '*' }),
        animate('0.2s', style({ opacity: 0, height: 0 }))
      ])
    ])
  ]
})
export class AddresFormComponent implements OnInit, AfterViewInit, OnChanges {
  @ViewChildren(ViggoCoreCustomInputComponent)
  inputElements: QueryList<ViggoCoreCustomInputComponent>;

  @ViewChildren('cep', { read: ElementRef })
  private cepInputs: QueryList<ElementRef<HTMLDivElement>>;

  @ViewChildren('numero', { read: ElementRef })
  private numeroInputs: QueryList<ElementRef<HTMLDivElement>>;

  @ViewChildren('complemento', { read: ElementRef })
  private complementoInputs: QueryList<ElementRef<HTMLDivElement>>;

  @ViewChildren('bairro', { read: ElementRef })
  private bairroInputs: QueryList<ElementRef<HTMLDivElement>>;

  @ViewChildren('logradouro', { read: ElementRef })
  private logradouroInputs: QueryList<ElementRef<HTMLDivElement>>;

  @ViewChildren('municipio', { read: ElementRef })
  private municipioInputs: QueryList<ElementRef<HTMLDivElement>>;

  public messageService: FormServiceValidationMessages;
  public displayMessage = {};
  public form: UntypedFormGroup;
  public enderecoList: UntypedFormArray;
  public domain_id: string;
  protected cepPipe: CepPipe;
  @Input() entity: IParceiro;

  @Input() readonly = false;
  @Input() notRequired = false;

  blocked_actions = false;

  constructor(
    public matSnackBar: MatSnackBar,
    public matDialog: MatDialog,
    protected router: Router,
    protected formBuilder: UntypedFormBuilder,
    protected cepService: CepService,
    protected tokenService: TokenService,
    protected municipioService: MunicipioService
  ) {
    this.messageService = new FormServiceValidationMessages(
      locationValidationMessages
    );
    this.cepPipe = new CepPipe();
  }

  ngOnChanges(): void {
    if (this.tokenService.isLoggedIn()) {
      this.ngOnInit();
      this.changeStep();
    }
  }

  ngOnInit(): void {
    let initFormValues = [];
    if (this.entity.enderecos)
      initFormValues = this.entity.enderecos.map(e =>
        this.createAddresNewForm(e)
      );
    else initFormValues = [this.createAddresNewForm()];
    // cria um endereço
    this.form = this.formBuilder.group({
      enderecos: this.formBuilder.array(initFormValues)
    });

    this.blocked_actions = localStorage.getItem('INTEGRACAO') === 'true';
    if (this.blocked_actions) {
      this.form.disable();
    }
    // cria um endereço
    // this.form = this.formBuilder.group({
    //   enderecos: this.formBuilder.array(
    //     this.entity.enderecos.map(e => this.createAddresNewForm(e))
    //   )
    // });

    this.enderecoList = this.form.controls.enderecos as UntypedFormArray;
  }

  public ngAfterViewInit(): void {
    const controlsBlurs = this.inputElements.map(el => el.blur);
    this.logradouroInputs
      .toArray()[0]
      .nativeElement.querySelector('input').readOnly = this.readonly;
    this.cepInputs
      .toArray()[0]
      .nativeElement.querySelector('input').readOnly = this.readonly;
    this.numeroInputs
      .toArray()[0]
      .nativeElement.querySelector('input').readOnly = this.readonly;

    this.complementoInputs
      .toArray()[0]
      .nativeElement.querySelector('input').readOnly = this.readonly;

    this.bairroInputs
      .toArray()[0]
      .nativeElement.querySelector('input').readOnly = this.readonly;

    this.changeStep();
    // observa as alterações nos forms para mostrar erros
    merge(this.enderecoList.valueChanges, ...controlsBlurs).subscribe(() => {
      this.displayMessage = this.messageService.getMessagesFormArray(
        this.enderecoList
      );
    });
  }

  /**
   * Cria um novo endereço dinamicamente
   * @param addres valor base
   */
  createAddresNewForm(
    addres: IParceiroEndereco = {} as IParceiroEndereco
  ): UntypedFormGroup {
    const form = this.formBuilder.group(
      this.readonly
        ? {
            logradouro: [addres.logradouro],
            numero: [addres.numero],
            complemento: [addres.complemento],
            bairro: [addres.bairro],
            cep: [addres.cep],
            ponto_referencia: [addres.ponto_referencia],
            municipio: [addres.municipio]
          }
        : this.notRequired
        ? {
            logradouro: [
              addres.logradouro,
              [Validators.maxLength(255), CustomValidators.isEmptyValue()]
            ],
            numero: [
              addres.numero,
              [
                Validators.pattern(Pattern.patternNumberAndLetters(1, 60)),
                Validators.maxLength(60),
                CustomValidators.isEmptyValue()
              ]
            ],
            complemento: [
              addres.complemento,
              [
                Validators.maxLength(60),
                Validators.minLength(1),
                CustomValidators.isEmptyValue()
              ]
            ],
            bairro: [
              addres.bairro,
              [Validators.maxLength(60), CustomValidators.isEmptyValue()]
            ],
            cep: [
              addres.cep,
              [
                Validators.pattern(Pattern.patternCep()),
                CustomValidators.isEmptyValue()
              ]
            ],
            ponto_referencia: [
              addres.ponto_referencia,
              [Validators.maxLength(500), CustomValidators.isEmptyValue()]
            ],
            municipio: [addres.municipio, [CustomValidators.typeIs('object')]]
          }
        : {
            logradouro: [
              addres.logradouro,
              [
                Validators.required,
                Validators.maxLength(255),
                CustomValidators.isEmptyValue()
              ]
            ],
            numero: [
              addres.numero,
              [
                Validators.required,
                Validators.pattern(Pattern.patternNumberAndLetters(1, 60)),
                Validators.maxLength(60),
                CustomValidators.isEmptyValue()
              ]
            ],
            complemento: [
              addres.complemento,
              [
                Validators.maxLength(60),
                Validators.minLength(1),
                CustomValidators.isEmptyValue()
              ]
            ],
            bairro: [
              addres.bairro,
              [
                Validators.maxLength(60),
                Validators.required,
                CustomValidators.isEmptyValue()
              ]
            ],
            cep: [
              addres.cep,
              [
                Validators.required,
                Validators.pattern(Pattern.patternCep()),
                CustomValidators.isEmptyValue()
              ]
            ],
            ponto_referencia: [
              addres.ponto_referencia,
              [Validators.maxLength(500), CustomValidators.isEmptyValue()]
            ],
            municipio: [
              addres.municipio,
              [CustomValidators.typeIs('object'), Validators.required]
            ]
          }
    );
    form.controls.municipio.valueChanges.subscribe((municipio?: IMunicipio) => {
      const cepForm = form.controls.cep;

      if (!municipio || !cepForm.value) cepForm.setValue('');
      if (typeof municipio !== 'object') cepForm.setValue('');
      if (municipio && municipio.cep)
        cepForm.setValue(this.cepPipe.transform(municipio.cep));
    });

    return form;
  }

  /**
   * Endereça o input para o campo de logradouro do form
   * @param index endereço do input que será focado
   */
  private focarNoLogradouro(index: number): void {
    // remove o foco do elemnto de auto complete
    this.municipioInputs
      .toArray()
      [index].nativeElement.querySelector('input')
      .blur();
    // foca no campo de logradouro
    this.logradouroInputs
      .toArray()
      [index].nativeElement.querySelector('input')
      .focus();
    // da o click no campo de logradouro para sair do campo de autocomplete
    this.logradouroInputs
      .toArray()
      [index].nativeElement.querySelector('input')
      .click();
  }
  /**
   * Pega o endereço a partir do cep com a api do correios
   * @param cep valor em string do cep
   * @param index endereço que está sendo modificado
   */
  checkCep(cep: string, index: number): void {
    const form = this.enderecoList.controls[index] as UntypedFormGroup;
    // verifica se o campo foi tocado pelo usuário e está válido
    if (form.controls.cep.valid && form.controls.cep.dirty) {
      // caso o municipio esteja setado, verifica se o cep inserido é igual o cep digitado

      form.controls.cep.setValue(this.cepPipe.transform(cep));
      // pega o cep pela api
      this.cepService.getAddres(cep).subscribe(
        addres => {
          // caso acheo local, preenche as informações dos campos
          const { controls } = form;

          if (addres.ibge && addres.localidade) {
            this.municipioService
              .getEntitiesByParams(`codigo_ibge=${addres.ibge}`)
              .subscribe(
                municipios => {
                  if (municipios && municipios.length)
                    controls.municipio.setValue(municipios[0]);
                  else controls.municipio.setValue(addres.localidade);
                  this.focarNoLogradouro(index);
                },
                (err: HttpErrorResponse) => {
                  this.displayError(err);
                }
              );
          }

          if (addres.logradouro)
            controls.logradouro.setValue(addres.logradouro);
          if (addres.bairro) controls.bairro.setValue(addres.bairro);
          if (addres.complemento)
            controls.complemento.setValue(addres.complemento);
        },
        () => {
          // caso não ache um endereço adiciona o erro ao input de cep
          form.controls.cep.setErrors({ notFound: true });
          this.changeStep();
        }
      );
    }
  }
  /**
   * Pega as informações para salvar o endereco sem o municipio
   */
  getValues(): IParceiroEndereco[] {
    const r = this.enderecoList.controls.map<IParceiroEndereco>(({ value }) =>
      this.notRequired
        ? {
            ...value,
            municipio: undefined,
            municipio_id: value.municipio ? value.municipio.id : undefined,
            cep: value.cep ? value.cep.replace(/\D/g, '') : undefined
          }
        : {
            ...value,
            municipio: undefined,
            municipio_id: value.municipio.id,
            cep: value.cep.replace(/\D/g, '')
          }
    );
    return r.map(v => Utils.trimAttributes(Utils.clearObject(v)));
  }
  /**
   * Chamado quando o formulário é alterado
   */
  changeStep(): void {
    this.form.markAllAsTouched();
    this.markAllControlsAsDirty();
    if (!this.form.valid) {
      this.displayMessage = this.messageService.getMessagesFormArray(
        this.enderecoList
      );
    }
  }

  markAllControlsAsDirty() {
    const form = this.enderecoList.controls[0] as UntypedFormGroup;
    Object.keys(form.controls).forEach(controlKey => {
      controlKey = String(controlKey);
      const control = form.get(controlKey);
      if (!control || control.valid) return;
      control.markAsDirty();
    });
  }

  public updateFormValue(enderecos: IParceiroEndereco[]): void {
    if (!enderecos || !enderecos.length) this.resetForm();
    else
      enderecos.forEach(value => {
        this.municipioService.getEntity(value.municipio_id).subscribe(
          municipio => {
            const updatedValue: IParceiroEndereco = {
              bairro: value.bairro || '',
              cep: value.cep || '',
              complemento: value.complemento || '',
              logradouro: value.logradouro || '',
              numero: value.numero || '',
              ponto_referencia: value.ponto_referencia || '',
              municipio
            } as IParceiroEndereco;
            this.enderecoList.setValue([{ ...updatedValue }]);
          },
          (err: HttpErrorResponse) => {
            this.displayError(err);
          }
        );
      });
  }

  private resetForm(): void {
    this.enderecoList.clear();
    this.enderecoList.push(this.createAddresNewForm());
  }
  /**
   * Formatador do municipio
   */
  displayFn = (municipio: IMunicipio | string): string => {
    if (!municipio) return '';
    if (typeof municipio === 'string') return municipio;
    return `${municipio.nome} - ${municipio.sigla_uf}`;
  };

  displayError(err: HttpErrorResponse, showDefaultMsg = true) {
    const errorMsg = new ErroMessages({
      route: this.router.url,
      router: this.router,
      matSnackBar: this.matSnackBar,
      httpError: err,
      showDefaultMsg: showDefaultMsg,
      loginService: null,
      tokenService: this.tokenService,
      dialogComponent: ConfirmDialogComponent,
      matDialog: this.matDialog,
      dialogData: {
        config: {
          data: {
            title: 'Aviso - Erro ao realizar operação',
            bodyMessage: err.error,
            icon: 'warning',
            icon_color: 'accent',
            singleBtn: true
          } as ConfirmDialogData,
          disableClose: true,
          width: '500px'
        } as MatDialogConfig<any>
      }
    } as IErroMessages);
    errorMsg.checkError();
  }
}
